import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEndpoints } from '../../../../hooks/useEndpoints';
import { useModuleVisibility } from '../../../../hooks/useModuleVisibility';
import { antifragilityEndpoints } from '../../../../endpoints/antifragility';
import {
  getMondayOf,
  normalizeTeamEffort,
} from '../../../../leopart-update/components/effort/useTeamEffort';
import { IAFITeamResponse, IAFITeamEntry } from '../../../../models/afi';
import { TeamReadinessResponse } from '../../../../leopart-update/types';

/** Metriche sintetiche + copertura dei moduli recenti per l'hub Panoramica (#18). */
export interface IOverviewHub {
  effort: {
    avgLoad: number | null;
    avgAcwr: number | null;
    outOfRangeCount: number;
    withData: number;
  } | null;
  readiness: {
    riMean: number | null;
    withData: number;
    total: number;
  } | null;
  afi: {
    mean: number | null;
    withData: number;
    atRisk: number;
  } | null;
  /** Visibilità per-modulo (feature-flag): l'hub mostra solo le card dei tab attivi. */
  visible: { sforzo: boolean; readiness: boolean; afi: boolean };
  loading: { sforzo: boolean; readiness: boolean; afi: boolean };
}

function average(values: number[]): number | null {
  if (values.length === 0) return null;
  return Math.round((values.reduce((s, v) => s + v, 0) / values.length) * 10) / 10;
}

/** Un'entry AFI ha "dati" se almeno un componente è valorizzato: gli atleti
 * senza alcun componente hanno un afiScore parziale/0 che falserebbe la media. */
const afiHasData = (a: IAFITeamEntry): boolean =>
  a.componentA != null || a.componentB != null || a.componentC != null;

/**
 * Dati sintetici per l'hub cliccabile della Panoramica squadra (#18): carico/
 * ACWR (tab Sforzo, settimana corrente), readiness di oggi, antifragilità (AFI).
 * Nessun endpoint nuovo: compone effort/readiness/afi. Le medie di squadra
 * dichiarano sempre la copertura (regola vault 2026-08-06: assenza = null, non 0)
 * e la media AFI esclude gli atleti senza dati. L'hub rispetta la
 * module-visibility: fetch e card solo per i moduli (tab) attivi.
 */
export const useOverviewHub = (teamId?: string): IOverviewHub => {
  const endpoints = useEndpoints();
  const { isTabVisible } = useModuleVisibility();
  const week = getMondayOf(dayjs());
  const today = dayjs().format('YYYY-MM-DD');

  const showEffort = isTabVisible('team-detail-sforzo');
  const showReadiness = isTabVisible('team-detail-readiness');
  const showAfi = isTabVisible('team-detail-afi');

  const effortQuery = useQuery({
    queryKey: ['overview-effort', teamId, week],
    queryFn: async () => {
      const { data } = await endpoints.getTeamEffort(teamId!, { week });
      return normalizeTeamEffort(data);
    },
    enabled: !!teamId && showEffort,
    staleTime: 1000 * 60 * 5,
  });

  const readinessQuery = useQuery<TeamReadinessResponse>({
    queryKey: ['overview-readiness', teamId, today],
    queryFn: async () =>
      (await endpoints.getTeamReadiness(teamId!, { date: today })).data as TeamReadinessResponse,
    enabled: !!teamId && showReadiness,
    staleTime: 1000 * 60 * 5,
  });

  const afiQuery = useQuery<IAFITeamResponse>({
    queryKey: ['overview-afi', teamId],
    queryFn: async () => (await antifragilityEndpoints.getTeamAFI(teamId!)).data as IAFITeamResponse,
    enabled: !!teamId && showAfi,
    staleTime: 1000 * 60 * 5,
  });

  const effortKpi = effortQuery.data?.kpi;
  const effortRows = effortQuery.data?.rows ?? [];
  const summary = readinessQuery.data?.summary;
  const afiValid = (afiQuery.data?.athletes ?? []).filter(afiHasData);

  return {
    effort: effortKpi
      ? {
          avgLoad: effortKpi.avgLoad,
          avgAcwr: effortKpi.avgAcwr,
          outOfRangeCount: effortKpi.outOfRangeCount ?? 0,
          withData: effortRows.filter((r) => (r.weekTotal ?? 0) > 0).length,
        }
      : null,
    readiness: summary
      ? {
          riMean: summary.riMean,
          withData: summary.athletesWithData ?? 0,
          total: summary.totalAthletes ?? 0,
        }
      : null,
    afi: afiQuery.data
      ? {
          mean: average(afiValid.map((a) => a.afiScore)),
          withData: afiValid.length,
          atRisk: afiValid.filter(
            (a) => a.afiClass === 'fragile' || a.afiClass === 'critical',
          ).length,
        }
      : null,
    visible: { sforzo: showEffort, readiness: showReadiness, afi: showAfi },
    loading: {
      sforzo: effortQuery.isLoading,
      readiness: readinessQuery.isLoading,
      afi: afiQuery.isLoading,
    },
  };
};
